import { useEffect, useRef } from 'react'

import { isZoomUrl } from './fallback'
import { StatusScreen } from './StatusScreen'
import { t } from './i18n'

/**
 * Where a class lands when the lesson falls back to Zoom mid-flight.
 *
 * The link is an ANCHOR, not a button that calls window.open. An anchor
 * navigation is a real user gesture and is never popup-blocked, and on a phone
 * it hands off to the installed Zoom app through the universal link. A button
 * wrapping window.open would be blocked for a meaningful share of students, and
 * this is not a screen anyone should have to troubleshoot.
 *
 * We still ATTEMPT to open Zoom automatically, because for the tutor — who
 * arrived here from their own tap — it works and saves them a step. It is
 * attempted exactly once, and the copy never promises it: browsers block
 * programmatic opens outside a user gesture, so for most students the tap
 * through is the real path. The button is therefore the primary element on this
 * screen, not a fallback tucked underneath one.
 */
export function MovedScreen({ url, host }: { url: string; host: boolean }) {
  const triedRef = useRef(false)
  /**
   * THE choke point for every URL this app will ever navigate a child to.
   *
   * Three paths reach this screen — the data-channel announce, the 409 on
   * re-exchange, and the tutor's own fallback response — and only the first one
   * validated its URL at the source. Checking here instead of at each entry
   * means there is exactly one place to audit, and no future caller can add a
   * fourth path that skips it.
   *
   * A URL that fails falls back to the pre-existing "open it from the iSchool
   * app" copy. That is the dead end this feature set out to remove, which is
   * exactly why it is the right failure mode: recoverable, and strictly better
   * than sending an 8-year-old somewhere we could not vouch for.
   */
  const safeUrl = isZoomUrl(url) ? url : null

  useEffect(() => {
    if (triedRef.current || !safeUrl) return
    triedRef.current = true
    try {
      window.open(safeUrl, '_blank', 'noopener,noreferrer')
    } catch {
      /* blocked, which is the expected case — the button below is the real path */
    }
  }, [safeUrl])

  if (!safeUrl) {
    return <StatusScreen title={t('error.switched.title')} detail={t('error.switched.detail')} />
  }

  return (
    <StatusScreen
      title={t(host ? 'moved.hostTitle' : 'moved.title')}
      detail={t(host ? 'moved.hostDetail' : 'moved.detail')}
    >
      <a className="primary-button" href={safeUrl} target="_blank" rel="noopener noreferrer">
        {t('moved.join')}
      </a>
    </StatusScreen>
  )
}
